import { useLayoutEffect, useRef, type ReactNode } from "react";
import { positionChipPopover } from "../lib/chipPopover.ts";

/**
 * A settings-in-context popover anchored under a function-bar chip.
 * The parent (`.admin2-function-bar`) is `position: relative`; this element is
 * absolutely positioned within it. Horizontal placement + arrow offset come
 * from the pure `positionChipPopover` helper.
 */
export function ChipPopover({
  id,
  anchor,
  container,
  onClose,
  title,
  children,
  hidden = false,
}: {
  id: string;
  anchor: HTMLElement | null;
  container: HTMLElement | null;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /**
   * When true the popover stays mounted but is visually and
   * accessibility-hidden. Positioning is skipped while hidden.
   */
  hidden?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (hidden || !anchor || !container || !ref.current) return;
    const c = anchor.getBoundingClientRect();
    const p = container.getBoundingClientRect();
    const popWidth = ref.current.offsetWidth;
    const { left, arrow } = positionChipPopover(
      { left: c.left, width: c.width },
      { left: p.left, width: p.width },
      popWidth,
    );
    // Clamp the arrow so it never detaches past the popover's right edge.
    const clampedArrow = Math.min(popWidth - 18, arrow);
    ref.current.style.left = `${left}px`;
    ref.current.style.setProperty("--arrow", `${clampedArrow}px`);
  }, [anchor, container, hidden]);

  return (
    <div
      ref={ref}
      className="chip-pop"
      data-testid={`chip-pop-${id}`}
      role="dialog"
      aria-modal="false"
      hidden={hidden}
    >
      <div className="chip-pop-head">
        {title && <h4>{title}</h4>}
        <button
          type="button"
          className="chip-pop-close"
          aria-label="Close"
          title="Close"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      {children}
    </div>
  );
}
